/**
 * Helper pur pour déterminer la liste des systèmes d'information
 * ( = "applications" côté backend ) auxquels un utilisateur a accès
 * effectif , à afficher sur la fiche utilisateur ( cf
 * {@code UserView} -> section "Systèmes d'information accessibles" ) .
 *
 * <p><b>Pourquoi</b> ( ticket UX ) : la fiche utilisateur d'un admin
 * OpenADOM affichait une liste vide pour ce champ , alors qu'un admin
 * a accès à toutes les applications sans exception . Cela provoquait
 * de la confusion ( "suis-je vraiment admin ? le droit est mal posé ?" ) .
 *
 * <p><b>Règle métier</b> :
 * <ul>
 *   <li>{@code isOpenAdomAdmin = true} -&gt; toutes les applications
 *       existantes du système ( accès global , bypass charte ) ;</li>
 *   <li>sinon -&gt; uniquement les applications dont l'identifiant
 *       apparaît dans le record {@code user.chartes} ( applications
 *       sur lesquelles l'utilisateur a explicitement signé la charte
 *       ou reçu un rôle ) .</li>
 * </ul>
 *
 * <p><b>SOLID</b> :
 * <ul>
 *   <li><b>SRP</b> : ce module fait UNIQUEMENT la résolution
 *       d'identifiants . L'affichage et le fetch des noms localisés
 *       restent dans le composant parent ;</li>
 *   <li><b>OCP</b> : ajouter une nouvelle source de droits ( ex
 *       super-admin organisationnel ) = ajouter un champ dans
 *       {@link AccessibleApplicationsInput} et une branche dans la
 *       fonction , sans toucher au reste du code ;</li>
 *   <li><b>DRY</b> : la règle "admin = toutes les apps" est centralisée
 *       ici , évitant des if éparpillés dans le code UI .</li>
 * </ul>
 */

export interface AccessibleApplicationsInput {
  /** Vrai si l'utilisateur a le droit global d'administration . */
  isOpenAdomAdmin?: boolean | null;
  /**
   * Identifiants d'applications déjà signés / accordés à l'utilisateur
   * via le mécanisme de chartes . Peut être {@code null} / {@code undefined}
   * ( aucune charte signée ) , auquel cas la liste effective est vide .
   */
  charteIds?: readonly string[] | null;
  /**
   * Identifiants de toutes les applications existantes dans le système ,
   * utilisée uniquement lorsque {@link isOpenAdomAdmin} est vrai . Peut
   * être vide pour un système qui démarre sans application .
   */
  allApplicationIds?: readonly string[] | null;
}

/**
 * Résout la liste effective des identifiants d'applications accessibles
 * à l'utilisateur , en retirant les doublons et les valeurs vides .
 * L'ordre relatif d'entrée est préservé pour rester prédictible côté UI .
 */
export function resolveAccessibleApplicationIds(
  input: AccessibleApplicationsInput | null | undefined,
): string[] {
  if (!input) return [];
  const source: readonly string[] | null | undefined = input.isOpenAdomAdmin
    ? input.allApplicationIds
    : input.charteIds;
  if (!Array.isArray(source)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of source) {
    if (typeof raw !== "string") continue;
    const id = raw.trim();
    if (!id) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}
